import { Component, Input, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef } from '@angular/core';

@Component({
  selector: 'app-custom-audio-player',
  standalone: false,
  template: `
    <div class="flex items-center gap-2.5 w-full min-w-[220px]" [ngClass]="isDarkTheme ? 'text-white' : 'text-slate-700'">
      
      <!-- Mic Icon -->
      <lucide-icon *ngIf="!isDarkTheme" name="mic" class="h-4 w-4 shrink-0" [class.text-blue-600]="true"></lucide-icon>

      <!-- Play/Pause Button -->
      <button (click)="togglePlay($event)" class="h-9 w-9 shrink-0 rounded-full flex items-center justify-center transition-transform active:scale-90 focus:outline-none"
        [ngClass]="isDarkTheme ? 'bg-white/20 hover:bg-white/30' : 'bg-blue-100 hover:bg-blue-200 text-blue-600'">
        <lucide-icon [name]="isPlaying ? 'pause' : 'play'" class="h-4 w-4" [class.ml-0.5]="!isPlaying && !isDarkTheme" [class.ml-1]="!isPlaying && isDarkTheme"></lucide-icon>
      </button>

      <!-- Slider & Time -->
      <div class="flex-1 flex flex-col justify-center min-w-0 px-1 opacity-90">
        <input type="range" [min]="0" [max]="duration || 100" [value]="currentTime" 
          (input)="seek($event)"
          class="w-full h-1.5 rounded-full appearance-none cursor-pointer focus:outline-none transition-all"
          [style.background]="getSliderBackground()"
          style="-webkit-appearance: none;">
          
        <div class="flex justify-between items-center text-[10px] mt-1.5 font-medium opacity-70 tracking-wide font-mono">
            <span>{{ formatTime(currentTime) }}</span>
            <span *ngIf="duration">{{ formatTime(duration) }}</span>
        </div>
      </div>

      <!-- Hidden Audio Element -->
      <audio #audioEl [src]="src" (timeupdate)="onTimeUpdate()" (loadedmetadata)="onLoadedMetadata()" (ended)="onEnded()" preload="metadata"></audio>
    </div>
  `,
  styles: [`
    input[type=range]::-webkit-slider-thumb {
      -webkit-appearance: none;
      height: 10px;
      width: 10px;
      border-radius: 50%;
      background: currentColor;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      transition: transform 0.1s;
    }
    input[type=range]::-webkit-slider-thumb:hover {
      transform: scale(1.3);
    }
  `]
})
export class CustomAudioPlayerComponent implements OnDestroy {
  @Input() src!: string;
  @Input() isDarkTheme = false;

  @ViewChild('audioEl') audioEl!: ElementRef<HTMLAudioElement>;

  isPlaying = false;
  currentTime = 0;
  duration = 0;

  constructor(private cdr: ChangeDetectorRef) {}

  togglePlay(event: Event) {
    event.stopPropagation();
    if (!this.audioEl?.nativeElement) return;
    
    if (this.isPlaying) {
      this.audioEl.nativeElement.pause();
    } else {
      this.audioEl.nativeElement.play().catch(e => console.error('Audio play error', e));
    }
    this.isPlaying = !this.isPlaying;
    this.cdr.detectChanges();
  }

  onTimeUpdate() {
    if (!this.audioEl?.nativeElement) return;
    this.currentTime = this.audioEl.nativeElement.currentTime;
    this.cdr.detectChanges();
  }

  onLoadedMetadata() {
    if (!this.audioEl?.nativeElement) return;
    const audio = this.audioEl.nativeElement;
    
    if (audio.duration && audio.duration !== Infinity && !isNaN(audio.duration)) {
      this.duration = audio.duration;
      this.cdr.detectChanges();
    } else {
       // OGG files on some browsers return Infinity initially. 
       // Trick: set currentTime far away to force metadata fetch, then Reset.
       audio.currentTime = 1e101; 
       setTimeout(() => {
           audio.currentTime = 0;
           if (!isNaN(audio.duration) && audio.duration !== Infinity) {
               this.duration = audio.duration;
           }
           this.cdr.detectChanges();
       }, 150);
    }
  }

  onEnded() {
    this.isPlaying = false;
    this.currentTime = 0;
    if (this.audioEl?.nativeElement) {
        this.audioEl.nativeElement.currentTime = 0;
    }
    this.cdr.detectChanges();
  }

  seek(event: any) {
    if (!this.audioEl?.nativeElement) return;
    event.stopPropagation();
    const value = parseFloat(event.target.value);
    this.audioEl.nativeElement.currentTime = value;
    this.currentTime = value;
    this.cdr.detectChanges();
  }

  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds) || seconds === Infinity) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m + ':' + s.toString().padStart(2, '0');
  }

  getSliderBackground() {
    const percentage = this.duration ? (this.currentTime / this.duration) * 100 : 0;
    const color = this.isDarkTheme ? 'rgba(255,255,255,1)' : '#3b82f6';
    const emptyColor = this.isDarkTheme ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.08)';
    return `linear-gradient(to right, ${color} ${percentage}%, ${emptyColor} ${percentage}%)`;
  }

  ngOnDestroy() {
    if (this.audioEl?.nativeElement) {
      this.audioEl.nativeElement.pause();
      this.audioEl.nativeElement.removeAttribute('src');
      this.audioEl.nativeElement.load();
    }
  }
}
